import React, { FC, useContext, useMemo } from 'react';
import { ListingInfoAside } from '../ListingInfoAside';
import LeadContext from '../../providers/Lead/Lead.context';
import StatementOfInformationCard from '../StatementOfInformationCard';
import {
  checkHasListing,
  checkIsUrgent,
  getAskingPrice,
  mapValueToMoneyFormat,
} from 'utils';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';
import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PROPERTY_TRANSLATION } from '../../../Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/pages/ListingInformationPage/utils';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';

type ListingLayoutProps = {
  hideSidePanel?: boolean;
  isBuying: boolean;
};

const ListingLayout: FC<ListingLayoutProps> = ({
  hideSidePanel,
  isBuying,
  children,
}) => {
  const { dealRoom, listing } = useContext(LeadContext);
  const {
    location,
    industry,
    revenue,
    EBITDA,
    averageStockValue,
    locationPostcode,
    type,
    dealLocation,
    askingPriceSelect,
    hasCommercialProperty,
    commercialProperty,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
    hasCommercialPropertyRequiredVisibleInfo,
  } = listing || {};

  const isEOI =
    askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST;
  const canIncludeStock = !isEOI || !isBuying;
  const showStockValue = canIncludeStock && averageStockValue;

  const askingPrice = useMemo(
    () => getAskingPrice(listing, !isBuying),
    [isBuying, listing],
  );

  const { t } = useTranslation();

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  const askingPriceVisibleInfo =
    askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE
      ? amountRequiredVisibleInfo
      : amountFromRequiredVisibleInfo || amountToRequiredVisibleInfo;

  const {
    isHidden: isAskingPriceHidden,
    reasonToHideContent: askingPriceReason,
  } = getShowFieldVisibleInfo(askingPriceVisibleInfo);

  const { isHidden: isRevenueHidden, reasonToHideContent: revenueReason } =
    getShowFieldVisibleInfo(revenueRequiredVisibleInfo);

  const { isHidden: isEBITDAHidden, reasonToHideContent: EBITDAReason } =
    getShowFieldVisibleInfo(EBITDARequiredVisibleInfo);

  const {
    isHidden: isAverageStockValueHidden,
    reasonToHideContent: averageStockValueReason,
  } = getShowFieldVisibleInfo(averageStockValueRequiredVisibleInfo);

  const {
    isHidden: isCommercialPropertyHidden,
    reasonToHideContent: commercialPropertyReason,
  } = getShowFieldVisibleInfo(hasCommercialPropertyRequiredVisibleInfo);

  const stockSuffix = showStockValue
    ? ` + stock (~${mapValueToMoneyFormat(averageStockValue)})`
    : canIncludeStock && isAverageStockValueHidden
    ? ` + stock (${averageStockValueReason})`
    : '';

  const amountDisplay = askingPrice
    ? `${askingPrice}${stockSuffix}`
    : isAskingPriceHidden
    ? askingPriceReason
    : null;

  const revenueDisplay = revenue
    ? `${mapValueToMoneyFormat(revenue)}`
    : isRevenueHidden
    ? revenueReason
    : null;

  const EBITDADisplay = EBITDA
    ? `${mapValueToMoneyFormat(EBITDA)}`
    : isEBITDAHidden
    ? EBITDAReason
    : null;

  const commercialPropertyDisplay = hasCommercialProperty
    ? t(COMMERCIAL_PROPERTY_TRANSLATION[commercialProperty])
    : isCommercialPropertyHidden
    ? commercialPropertyReason
    : null;

  return (
    <>
      {!hideSidePanel && <ListingInfoAside />}
      <main>
        {children}

        {isBuying &&
          checkHasListing(dealRoom?.listingStage) &&
          !checkIsUrgent(type) && (
            <StatementOfInformationCard
              statementOfInformation={{
                amount: amountDisplay,
                industry,
                locationPostcode,
                location: location?.state || dealLocation,
                revenue: revenueDisplay,
                EBITDA: EBITDADisplay,
                ...(commercialPropertyDisplay && {
                  ownersAlsoOwnCommercialProperty: commercialPropertyDisplay,
                }),
              }}
            />
          )}
      </main>
    </>
  );
};

export default ListingLayout;
